import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaTimes, FaTrophy, FaFlag, FaUsers, FaGavel, FaMedal, FaUser as FaUserIcon, FaUserEdit, FaArrowLeft, FaExclamationTriangle } from '../components/Icons';
import { FaUserShield, FaUserLock, FaUserCog, FaCalendarAlt, FaCalculator, FaRobot, FaForward, FaBook, FaImage, FaExclamationCircle, FaBars } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import { PERMISSIONS, ROUTE_PERMISSIONS } from '../models/authTypes';

interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  opacity: ${props => props.$isOpen ? 1 : 0};
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  height: 100vh;
  background-color: #1a1a2e;
  color: white;
  z-index: 3000;
  transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  transition: transform 0.3s ease;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  overflow-y: auto;
  border-right: 4px solid ${(props) => props.theme?.colors?.secondary?.main || '#e4312e'};
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #16213e;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Logo = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: white;
  
  .vr {
    color: ${(props) => props.theme?.colors?.secondary?.main || '#e4312e'};
  }
`;

const MenuContent = styled.div`
  flex: 1;
  padding: 16px 0;
`;

const MenuSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 12px 16px;
`;

const MenuItem = styled.div<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  
  a {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    color: ${props => (props.$isActive ? (props.theme?.colors?.secondary?.main || '#e4312e') : 'white')};
    text-decoration: none;
    font-size: 16px;
    transition: all 0.2s ease;
    width: 100%;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      color: ${(props) => props.theme?.colors?.secondary?.main || '#e4312e'};
    }
    
    svg {
      font-size: 18px;
      min-width: 18px;
    }
  }
`;

const UserSection = styled.div`
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background-color: #16213e;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${(props) => props.theme?.colors?.secondary?.main || '#e4312e'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: bold;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserDetails = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: white;
`;

const UserRole = styled.div`
  font-size: 14px;
  color: #888;
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #c82333;
  }
`;

const MobileNavigation: React.FC<MobileNavigationProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    console.log('[MobileNavigation] Rendered. isOpen:', isOpen);
  }, [isOpen]);
  useEffect(() => {
    console.log('MobileNavigation: isOpen=', isOpen);
  }, [isOpen]);
  const location = useLocation();
  const { currentUser, hasPermission, hasAnyRole } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      onClose();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleLinkClick = () => {
    onClose();
  };

  // Cerrar menú al cambiar de ruta
  const firstRender = React.useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    onClose();
  }, [location.pathname, onClose]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <MobileMenu $isOpen={isOpen}>

        <MobileHeader>
          <Logo>
            <span className="vr">VR</span>
            <span className="simracers">Simracers</span>
          </Logo>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </MobileHeader>

        <MenuContent>
          <MenuItem $isActive={isActive('/')}>
            <Link to="/" onClick={handleLinkClick}>
              <FaTrophy /> Inicio
            </Link>
          </MenuItem>

          {/* Sección Principal */}
          <MenuSection>
            <SectionTitle>Principal</SectionTitle>
            
            <MenuItem $isActive={isActive('/ligas')}>
              <Link to="/ligas" onClick={handleLinkClick}>
                <FaTrophy /> Ligas
              </Link>
            </MenuItem>

            <MenuItem $isActive={isActive('/carreras')}>
              <Link to="/carreras" onClick={handleLinkClick}>
                <FaFlag /> Carreras
              </Link>
            </MenuItem>

            <MenuItem $isActive={isActive('/clasificaciones')}>
              <Link to="/clasificaciones" onClick={handleLinkClick}>
                <FaMedal /> Clasificaciones
              </Link>
            </MenuItem>

            {hasPermission('view:lastres') && (
              <MenuItem $isActive={location.pathname.includes('/lastres')}>
                <Link to="/lastres" onClick={handleLinkClick}>
                  <FaCalculator /> Lastres
                </Link>
              </MenuItem>
            )}

            <MenuItem $isActive={isActive('/socios')}>
              <Link to="/socios" onClick={handleLinkClick}>
                <FaUsers /> Pilotos
              </Link>
            </MenuItem>

            {hasPermission(PERMISSIONS.VIEW_EQUIPOS) && (
              <MenuItem $isActive={isActive('/equipos')}>
                <Link to="/equipos" onClick={handleLinkClick}>
                  <FaUsers /> Equipos
                </Link>
              </MenuItem>
            )}
          </MenuSection>

          {/* Sección Gestión */}
          <MenuSection>
            <SectionTitle>Gestión</SectionTitle>
            
            {hasPermission(PERMISSIONS.VIEW_PUNTUACIONES) && (
              <MenuItem $isActive={isActive('/puntuaciones')}>
                <Link to="/puntuaciones" onClick={handleLinkClick}>
                  <FaMedal /> Puntuaciones
                </Link>
              </MenuItem>
            )}

            {hasPermission(PERMISSIONS.VIEW_SANCIONES) && (
              <MenuItem $isActive={isActive('/sanciones')}>
                <Link to="/sanciones" onClick={handleLinkClick}>
                  <FaGavel /> Sanciones
                </Link>
              </MenuItem>
            )}

            {hasPermission('view:penalty_rules') && (
              <MenuItem $isActive={isActive('/sanciones/reglas-automaticas')}>
                <Link to="/sanciones/reglas-automaticas" onClick={handleLinkClick}>
                  <FaRobot /> Reglas Automáticas
                </Link>
              </MenuItem>
            )}

            {hasPermission(PERMISSIONS.VIEW_RECLAMACIONES) && (
              <MenuItem $isActive={isActive('/reclamaciones')}>
                <Link to="/reclamaciones" onClick={handleLinkClick}>
                  <FaExclamationTriangle /> Reclamaciones
                </Link>
              </MenuItem>
            )}

            {hasPermission(PERMISSIONS.VIEW_APELACIONES) && (
              <MenuItem $isActive={isActive('/apelaciones')}>
                <Link to="/apelaciones" onClick={handleLinkClick}>
                  <FaExclamationCircle /> Apelaciones
                </Link>
              </MenuItem>
            )}
          </MenuSection>

          {/* Sección Herramientas */}
          <MenuSection>
            <SectionTitle>Herramientas</SectionTitle>
            
            {hasPermission(PERMISSIONS.VIEW_EVENTOS) && (
              <MenuItem $isActive={isActive('/eventos')}>
                <Link to="/eventos" onClick={handleLinkClick}>
                  <FaCalendarAlt /> Calendario
                </Link>
              </MenuItem>
            )}

            {hasAnyRole(['admin', 'superadmin']) && (
              <MenuItem $isActive={isActive('/miniaturas/generador-miniaturas')}>
                <Link to="/miniaturas/generador-miniaturas" onClick={handleLinkClick}>
                  <FaImage /> Miniaturas
                </Link>
              </MenuItem>
            )}

            {hasPermission('view:ia_redaccion_circuitos') && (
              <MenuItem $isActive={isActive('/ia/redaccion-circuitos')}>
                <Link to="/ia/redaccion-circuitos" onClick={handleLinkClick}>
                  <FaRobot /> Redacción IA
                </Link>
              </MenuItem>
            )}

            {hasPermission(PERMISSIONS.VIEW_BLOG) && (
              <MenuItem $isActive={isActive('/blog')}>
                <Link to="/blog" onClick={handleLinkClick}>
                  <FaBook /> Blog
                </Link>
              </MenuItem>
            )}

            {hasPermission('view:warmup') && (
              <MenuItem $isActive={isActive('/warmup')}>
                <Link to="/warmup" onClick={handleLinkClick}>
                  <FaForward /> WarmUp
                </Link>
              </MenuItem>
            )}

            {hasPermission('view:guide') && (
              <MenuItem $isActive={isActive('/guia-bienvenida')}>
                <Link to="/guia-bienvenida" onClick={handleLinkClick}>
                  <FaBook /> Guía Primavera 2025
                </Link>
              </MenuItem>
            )}
          </MenuSection>

          {/* Sección Administración */}
          {hasAnyRole(['admin', 'superadmin']) && (
            <MenuSection>
              <SectionTitle>Administración</SectionTitle>
              
              {hasPermission('admin:users') && (
                <MenuItem $isActive={isActive('/admin/usuarios')}>
                  <Link to="/admin/usuarios" onClick={handleLinkClick}>
                    <FaUserShield /> Usuarios
                  </Link>
                </MenuItem>
              )}

              <MenuItem $isActive={isActive('/admin/roles')}>
                <Link to="/admin/roles" onClick={handleLinkClick}>
                  <FaUserCog /> Permisos
                </Link>
              </MenuItem>
            </MenuSection>
          )}
        </MenuContent>

        {currentUser && (
          <UserSection>
            <UserInfo>
              <UserAvatar>
                {currentUser.photoURL ? (
                  <img src={currentUser.photoURL} alt={currentUser.displayName || 'Usuario'} />
                ) : (
                  <FaUserIcon />
                )}
              </UserAvatar>
              <UserDetails>
                <UserName>{currentUser.displayName || 'Usuario'}</UserName>
                <UserRole>{currentUser.email}</UserRole>
              </UserDetails>
            </UserInfo>
            <LogoutButton onClick={handleLogout}>
              Cerrar Sesión
            </LogoutButton>
          </UserSection>
        )}
      </MobileMenu>
    </>
  );
};

export default MobileNavigation;
